import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import MinistryCard from './components/MinistryCard';
import VideoModal from './components/VideoModal';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type MainTab = 'church' | 'sermons' | 'media' | 'private';
type MediaSubTab = 'movies' | 'songs';

export default function Ministries() {
  const { t } = useTranslation();
  const [activeMainTab, setActiveMainTab] = useState<MainTab>('church');
  const [activeMediaSubTab, setActiveMediaSubTab] = useState<MediaSubTab>('movies');
  const [selectedLanguage, setSelectedLanguage] = useState('All');
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{ title: string; videoId: string } | null>(null);

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const ministries = [
    {
      id: 1,
      name: 'Adventist Youth Ministries (AYM)',
      description: 'Empowering young people aged 16-30 to grow in faith, leadership, and service',
      icon: 'ri-user-star-line',
      color: 'from-blue-500 to-indigo-600',
      category: 'Youth',
      image: 'https://public.readdy.ai/ai/img_res/23d3eba66be0e447d1f1395d45e91a98.jpg',
      leader: 'Pastor David Okonkwo',
      contact: 'aym@sdanigeria.org',
      programs: ['Pathfinders', 'Adventurers', 'Youth Sabbath School', 'Leadership Training', 'Community Service Projects', 'Youth Camps'],
      meetingTime: 'Saturdays, 9:30 AM',
      location: 'Youth Hall, Main Campus',
      fullDescription: 'Adventist Youth Ministries (AYM) is dedicated to nurturing the spiritual, physical, mental, and social development of young people. We provide opportunities for youth to develop leadership skills, engage in mission work, and build lasting friendships centered on Christ.',
      objectives: [
        'Develop strong Christian character and leadership in young people',
        'Provide spiritual guidance and mentorship opportunities',
        'Engage youth in community service and evangelism',
        'Foster a supportive faith community for young Adventists'
      ],
      howToJoin: 'Join us every Saturday morning for Youth Sabbath School, or contact our youth leader to learn about Pathfinders, Adventurers, and upcoming youth programs.'
    },
    {
      id: 2,
      name: 'Adventist Women\'s Ministries (AWM)',
      description: 'Supporting and empowering women in their spiritual journey and service',
      icon: 'ri-women-line',
      color: 'from-pink-500 to-rose-600',
      category: 'Women',
      image: 'https://readdy.ai/api/search-image?query=african%20adventist%20women%20gathering%20in%20prayer%20circle%20with%20bibles%20showing%20sisterhood%20faith%20community%20support%20spiritual%20growth%20and%20service%20in%20bright%20welcoming%20church%20environment%20with%20diverse%20age%20groups&width=800&height=600&seq=awm-ministry-001&orientation=landscape',
      leader: 'Sister Grace Adeyemi',
      contact: 'awm@sdanigeria.org',
      programs: ['Prayer Ministries', 'Bible Study Groups', 'Health & Temperance', 'Community Outreach', 'Women\'s Retreats', 'Mentorship Programs'],
      meetingTime: 'First Sunday of each month, 3:00 PM',
      location: 'Fellowship Hall',
      fullDescription: 'Adventist Women\'s Ministries (AWM) exists to uphold, encourage, and challenge Adventist women in their pilgrimage as disciples of Jesus Christ and members of His world church. We focus on spiritual growth, health, family, and community service.',
      objectives: [
        'Nurture women\'s relationship with God through prayer and Bible study',
        'Build strong sisterhood and support networks',
        'Empower women in leadership and ministry',
        'Promote health and wellness from a wholistic perspective'
      ],
      howToJoin: 'All women are welcome! Attend our monthly meetings or contact Sister Grace for more information about our prayer groups, Bible studies, and service projects.'
    },
    {
      id: 3,
      name: 'Adventist Men\'s Ministries (AMM)',
      description: 'Building strong men of faith, character, and service',
      icon: 'ri-user-line',
      color: 'from-teal-500 to-emerald-600',
      category: 'Men',
      image: 'https://readdy.ai/api/search-image?query=african%20adventist%20men%20in%20bible%20study%20group%20discussion%20showing%20brotherhood%20leadership%20and%20faith%20with%20open%20bibles%20engaged%20conversation%20mentorship%20and%20service%20in%20modern%20church%20setting%20with%20natural%20lighting&width=800&height=600&seq=amm-ministry-001&orientation=landscape',
      leader: 'Elder Samuel Nwosu',
      contact: 'amm@sdanigeria.org',
      programs: ['Men\'s Breakfast Fellowship', 'Bible Study', 'Mentorship Program', 'Service Projects', 'Men\'s Retreats', 'Evangelism Training'],
      meetingTime: 'Second Saturday of each month, 7:00 AM',
      location: 'Conference Room',
      fullDescription: 'Adventist Men\'s Ministries (AMM) focuses on developing godly men who lead their families, churches, and communities with integrity, wisdom, and compassion. We emphasize spiritual growth, accountability, and practical service.',
      objectives: [
        'Strengthen men\'s relationship with God and His Word',
        'Develop leadership and mentorship skills',
        'Support men in their roles as husbands, fathers, and leaders',
        'Engage in meaningful service to church and community'
      ],
      howToJoin: 'Join us for our monthly breakfast meetings or contact Elder Samuel to get involved in our mentorship, Bible study, and service programs.'
    },
    {
      id: 4,
      name: 'Children\'s Ministries',
      description: 'Nurturing young hearts to love Jesus through age-appropriate programs',
      icon: 'ri-bear-smile-line',
      color: 'from-yellow-500 to-orange-600',
      category: 'Children',
      image: 'https://readdy.ai/api/search-image?query=african%20children%20in%20colorful%20adventist%20sabbath%20school%20classroom%20learning%20bible%20stories%20with%20teacher%20showing%20joy%20excitement%20and%20engagement%20with%20educational%20materials%20bright%20cheerful%20environment%20and%20interactive%20activities&width=800&height=600&seq=children-ministry-001&orientation=landscape',
      leader: 'Teacher Mary Okafor',
      contact: 'children@sdanigeria.org',
      programs: ['Sabbath School', 'Vacation Bible School', 'Children\'s Choir', 'Story Time', 'Cradle Roll', 'Kindergarten Division'],
      meetingTime: 'Every Saturday, 9:30 AM',
      location: 'Children\'s Wing',
      fullDescription: 'Children\'s Ministries creates a fun, safe, and engaging environment where children learn about Jesus through stories, songs, crafts, and activities. We partner with parents to provide age-appropriate spiritual education for children from birth through pre-teen years.',
      objectives: [
        'Teach children about God\'s love in age-appropriate ways',
        'Create a safe and nurturing learning environment',
        'Develop children\'s faith through interactive activities',
        'Partner with parents in spiritual education and character development'
      ],
      howToJoin: 'Bring your children to Sabbath School every Saturday morning. Parents interested in volunteering can contact Teacher Mary for training and opportunities.'
    },
    {
      id: 5,
      name: 'Health Ministries',
      description: 'Promoting wholistic health and wellness - body, mind, and spirit',
      icon: 'ri-heart-pulse-line',
      color: 'from-green-500 to-teal-600',
      category: 'Health',
      image: 'https://readdy.ai/api/search-image?query=african%20community%20health%20fair%20with%20people%20exercising%20doing%20yoga%20healthy%20cooking%20demonstrations%20showing%20wellness%20lifestyle%20preventive%20healthcare%20and%20wholistic%20health%20education%20in%20bright%20outdoor%20church%20setting&width=800&height=600&seq=health-ministry-001&orientation=landscape',
      leader: 'Dr. Chioma Eze',
      contact: 'health@sdanigeria.org',
      programs: ['Health Screenings', 'Cooking Classes', 'Exercise Groups', 'Mental Health Support', 'Nutrition Seminars', 'CHIP Program', 'Smoking Cessation'],
      meetingTime: 'Third Sunday of each month, 2:00 PM',
      location: 'Community Center',
      fullDescription: 'Health Ministries promotes the Adventist philosophy of wholistic health, addressing physical, mental, social, and spiritual wellness. We believe the body is the temple of the Holy Spirit and offer practical programs for disease prevention and healthy living.',
      objectives: [
        'Educate members about preventive health care and healthy lifestyle',
        'Promote the eight natural remedies (NEWSTART principles)',
        'Provide health screenings and wellness assessments',
        'Support mental, emotional, and spiritual well-being'
      ],
      howToJoin: 'Attend our monthly health seminars or join one of our ongoing wellness programs like CHIP (Complete Health Improvement Program). Contact Dr. Chioma for more information.'
    },
    {
      id: 6,
      name: 'Music Ministries',
      description: 'Worshiping God through music and song in diverse expressions',
      icon: 'ri-music-line',
      color: 'from-purple-500 to-pink-600',
      category: 'Music',
      image: 'https://readdy.ai/api/search-image?query=african%20adventist%20church%20choir%20singing%20with%20passion%20and%20joy%20wearing%20robes%20with%20musical%20instruments%20worship%20team%20showing%20unity%20praise%20spiritual%20expression%20in%20modern%20church%20sanctuary%20with%20professional%20sound%20system&width=800&height=600&seq=music-ministry-001&orientation=landscape',
      leader: 'Choir Director Emmanuel Bello',
      contact: 'music@sdanigeria.org',
      programs: ['Adult Choir', 'Youth Choir', 'Children\'s Choir', 'Praise Team', 'Instrumental Ensemble', 'Music Lessons', 'Handbell Choir'],
      meetingTime: 'Wednesdays, 6:00 PM (Choir Practice)',
      location: 'Main Sanctuary',
      fullDescription: 'Music Ministries leads the congregation in worship through diverse musical expressions, from traditional hymns to contemporary praise songs. We believe music is a powerful tool for worship, evangelism, and spiritual growth.',
      objectives: [
        'Lead meaningful worship through quality music ministry',
        'Develop musical talents within the congregation',
        'Provide opportunities for musical expression and service',
        'Enhance worship services with Spirit-filled music'
      ],
      howToJoin: 'Join us for choir practice on Wednesday evenings. All voices and instruments are welcome! Contact Emmanuel to audition or learn more about our various music groups.'
    },
    {
      id: 7,
      name: 'Family Ministries',
      description: 'Strengthening families through Christ-centered programs and support',
      icon: 'ri-parent-line',
      color: 'from-indigo-500 to-purple-600',
      category: 'Family',
      image: 'https://readdy.ai/api/search-image?query=african%20adventist%20family%20with%20parents%20and%20children%20praying%20together%20at%20home%20showing%20love%20unity%20and%20faith%20with%20warm%20lighting%20peaceful%20atmosphere%20representing%20strong%20christian%20family%20values%20and%20togetherness&width=800&height=600&seq=family-ministry-001&orientation=landscape',
      leader: 'Pastor & Mrs. Johnson',
      contact: 'family@sdanigeria.org',
      programs: ['Marriage Enrichment', 'Parenting Workshops', 'Family Game Nights', 'Couples Retreats', 'Family Counseling', 'Pre-marital Counseling'],
      meetingTime: 'Last Friday of each month, 6:00 PM',
      location: 'Fellowship Hall',
      fullDescription: 'Family Ministries supports and strengthens families at every stage, providing resources, education, and community for building Christ-centered homes. We believe strong families build strong churches and communities.',
      objectives: [
        'Strengthen marriages and family relationships',
        'Provide parenting education and support resources',
        'Create opportunities for family bonding and worship',
        'Offer counseling and crisis intervention support'
      ],
      howToJoin: 'Join us for our monthly family events or contact the Johnsons for marriage enrichment, parenting resources, and family counseling services.'
    },
    {
      id: 8,
      name: 'Community Services (ACS)',
      description: 'Serving our community with Christ\'s love through practical assistance',
      icon: 'ri-hand-heart-line',
      color: 'from-rose-500 to-red-600',
      category: 'Outreach',
      image: 'https://readdy.ai/api/search-image?query=african%20adventist%20volunteers%20serving%20community%20with%20food%20distribution%20clothing%20drive%20helping%20elderly%20showing%20compassion%20charity%20christian%20service%20smiling%20faces%20community%20engagement%20and%20practical%20assistance&width=800&height=600&seq=acs-ministry-001&orientation=landscape',
      leader: 'Deacon Peter Obi',
      contact: 'acs@sdanigeria.org',
      programs: ['Food Bank', 'Clothing Distribution', 'Homeless Outreach', 'Disaster Relief', 'Literacy Programs', 'Job Training', 'Medical Clinics'],
      meetingTime: 'Every Sunday, 10:00 AM',
      location: 'Community Services Center',
      fullDescription: 'Adventist Community Services (ACS) extends Christ\'s compassion to those in need through practical assistance, disaster relief, and ongoing support programs. We serve without discrimination, meeting people at their point of need.',
      objectives: [
        'Meet practical needs in the community with dignity and respect',
        'Demonstrate Christ\'s love through compassionate service',
        'Provide disaster relief and emergency assistance',
        'Partner with local organizations for greater community impact'
      ],
      howToJoin: 'Volunteer opportunities are available every Sunday. Contact Deacon Peter to learn how you can serve your community through food distribution, clothing drives, or other service projects.'
    },
    {
      id: 9,
      name: 'Sabbath School Ministries',
      description: 'Bible study and fellowship for all ages every Sabbath morning',
      icon: 'ri-book-open-line',
      color: 'from-amber-500 to-orange-600',
      category: 'Education',
      image: 'https://readdy.ai/api/search-image?query=african%20adventist%20sabbath%20school%20class%20with%20diverse%20age%20groups%20studying%20bible%20together%20with%20quarterly%20lesson%20guides%20showing%20engagement%20discussion%20and%20spiritual%20learning%20in%20welcoming%20church%20classroom%20setting&width=800&height=600&seq=sabbath-school-001&orientation=landscape',
      leader: 'Elder James Adebayo',
      contact: 'sabbathschool@sdanigeria.org',
      programs: ['Adult Sabbath School', 'Youth Sabbath School', 'Children\'s Divisions', 'Mission Stories', 'Lesson Study', 'Investment Program'],
      meetingTime: 'Every Saturday, 9:30 AM',
      location: 'Various Classrooms',
      fullDescription: 'Sabbath School is the Bible study and fellowship arm of the church, meeting every Sabbath morning before worship service. We provide age-appropriate Bible study for all members, from cradle roll to senior adults.',
      objectives: [
        'Provide systematic Bible study for all age groups',
        'Foster fellowship and community among members',
        'Support world mission through offerings and investment',
        'Develop Bible knowledge and spiritual growth'
      ],
      howToJoin: 'Join us every Saturday morning at 9:30 AM. Classes are available for all ages. Contact Elder James for class placement and quarterly lesson materials.'
    },
    {
      id: 10,
      name: 'Personal Ministries (PM)',
      description: 'Equipping members for evangelism and soul-winning',
      icon: 'ri-user-heart-line',
      color: 'from-cyan-500 to-blue-600',
      category: 'Evangelism',
      image: 'https://readdy.ai/api/search-image?query=african%20adventist%20members%20engaged%20in%20evangelism%20training%20bible%20studies%20and%20personal%20witnessing%20showing%20dedication%20to%20sharing%20faith%20with%20community%20outreach%20materials%20and%20spiritual%20conversations&width=800&height=600&seq=personal-ministries-001&orientation=landscape',
      leader: 'Elder Grace Nnamdi',
      contact: 'pm@sdanigeria.org',
      programs: ['Bible Study Training', 'Evangelism Seminars', 'Literature Ministry', 'Visitation Program', 'Small Groups', 'Prophecy Seminars'],
      meetingTime: 'Second Wednesday of each month, 6:00 PM',
      location: 'Conference Room',
      fullDescription: 'Personal Ministries equips and mobilizes every church member for active participation in evangelism and soul-winning. We believe every member is a minister and provide training and resources for effective witnessing.',
      objectives: [
        'Train members in effective evangelism methods',
        'Coordinate church-wide evangelistic activities',
        'Distribute literature and conduct Bible studies',
        'Support small group ministries and outreach programs'
      ],
      howToJoin: 'Attend our monthly training sessions or contact Elder Grace to learn about giving Bible studies, conducting evangelistic meetings, and sharing your faith effectively.'
    },
  ];

  const sermons = [
    {
      id: 1,
      title: 'Walking in Faith',
      speaker: 'Pastor John Adebayo',
      ministry: 'Main Church',
      date: 'Jan 15, 2025',
      duration: '45 min',
      language: 'EN',
      translations: ['YO', 'HA'],
      thumbnail: 'https://readdy.ai/api/search-image?query=african%20pastor%20preaching%20with%20passion%20in%20modern%20church%20sanctuary%20with%20congregation%20listening%20attentively%20warm%20lighting%20and%20spiritual%20atmosphere&width=600&height=400&seq=sermon-001&orientation=landscape',
      videoId: 'dQw4w9WgXcQ'
    },
    {
      id: 2,
      title: 'Ìgbàgbọ́ àti Ìrètí',
      speaker: 'Pastor Oluwaseun Ajayi',
      ministry: 'Yoruba Service',
      date: 'Jan 12, 2025',
      duration: '52 min',
      language: 'YO',
      translations: ['EN'],
      thumbnail: 'https://readdy.ai/api/search-image?query=nigerian%20yoruba%20pastor%20delivering%20sermon%20with%20bible%20in%20traditional%20church%20setting%20showing%20cultural%20elements%20and%20spiritual%20teaching&width=600&height=400&seq=sermon-002&orientation=landscape',
      videoId: 'abc123xyz'
    },
    {
      id: 3,
      title: 'The Power of Prayer',
      speaker: 'Elder Grace Nnamdi',
      ministry: 'Women\'s Ministry',
      date: 'Jan 10, 2025',
      duration: '38 min',
      language: 'EN',
      translations: ['IG', 'YO'],
      thumbnail: 'https://readdy.ai/api/search-image?query=african%20woman%20elder%20teaching%20about%20prayer%20with%20open%20bible%20and%20praying%20hands%20showing%20spiritual%20wisdom%20and%20faith%20leadership&width=600&height=400&seq=sermon-003&orientation=landscape',
      videoId: 'xyz789abc'
    },
  ];

  const movies = [
    {
      id: 1,
      title: 'Tell the World',
      description: 'The inspiring story of the Seventh-day Adventist Church',
      duration: '120 min',
      language: 'EN',
      category: 'Documentary',
      platform: 'YouTube',
      translations: [],
      thumbnail: 'https://readdy.ai/api/search-image?query=historical%20christian%20documentary%20movie%20poster%20showing%20early%20adventist%20pioneers%20with%20vintage%20photography%20style%20warm%20sepia%20tones%20and%20inspiring%20faith%20journey%20narrative&width=600&height=400&seq=tell-world-movie-001&orientation=landscape',
      videoId: 'kGKqcD0BMxs'
    },
    {
      id: 2,
      title: 'Ìjìnlẹ̀ Ọ̀rọ̀ Ìyìn',
      description: 'Nigerian Christian drama exploring faith and redemption',
      duration: '95 min',
      language: 'YO',
      category: 'Drama',
      platform: 'YouTube',
      translations: ['EN'],
      thumbnail: 'https://readdy.ai/api/search-image?query=nigerian%20yoruba%20christian%20drama%20movie%20poster%20with%20african%20actors%20in%20traditional%20and%20modern%20clothing%20showing%20emotional%20faith%20journey%20with%20warm%20cultural%20colors%20and%20spiritual%20themes&width=600&height=400&seq=yoruba-drama-001&orientation=landscape',
      videoId: 'dQw4w9WgXcQ'
    },
    {
      id: 3,
      title: 'The Great Controversy',
      description: 'Epic biblical documentary on spiritual warfare',
      duration: '110 min',
      language: 'EN',
      category: 'Documentary',
      platform: 'YouTube',
      translations: ['YO', 'HA'],
      thumbnail: 'https://readdy.ai/api/search-image?query=epic%20biblical%20documentary%20poster%20showing%20spiritual%20warfare%20theme%20with%20dramatic%20lighting%20ancient%20scrolls%20and%20prophetic%20imagery%20representing%20good%20versus%20evil%20in%20christian%20theology&width=600&height=400&seq=great-controversy-001&orientation=landscape',
      videoId: 'abc123xyz'
    },
    {
      id: 4,
      title: 'Ọkàn Tuntun',
      description: 'A story of transformation and new beginnings',
      duration: '88 min',
      language: 'YO',
      category: 'Inspirational',
      platform: 'YouTube',
      translations: ['EN', 'IG'],
      thumbnail: 'https://readdy.ai/api/search-image?query=nigerian%20yoruba%20inspirational%20movie%20poster%20showing%20transformation%20journey%20with%20african%20protagonist%20in%20hopeful%20pose%20warm%20sunrise%20lighting%20and%20cultural%20elements%20representing%20new%20life%20and%20faith&width=600&height=400&seq=okan-tuntun-001&orientation=landscape',
      videoId: 'xyz789abc'
    },
  ];

  const songs = [
    {
      id: 1,
      title: 'Worthy is the Lamb',
      artist: 'SDA Nigeria Choir',
      language: 'EN',
      translations: ['YO'],
      duration: '4:32',
      platform: 'Spotify',
      thumbnail: 'https://readdy.ai/api/search-image?query=professional%20church%20choir%20album%20cover%20with%20Nigerian%20singers%20in%20white%20robes%20holding%20songbooks%20warm%20golden%20lighting%20elegant%20typography%20worship%20music%20theme&width=400&height=300&seq=song-001&orientation=landscape',
    },
    {
      id: 2,
      title: 'Ọlọ́run Mi Tóbi',
      artist: 'Axios Worship',
      language: 'YO',
      translations: ['EN', 'IG'],
      duration: '5:15',
      platform: 'YouTube Music',
      thumbnail: 'https://readdy.ai/api/search-image?query=contemporary%20Nigerian%20worship%20music%20album%20art%20with%20vibrant%20colors%20African%20patterns%20modern%20design%20elements%20praise%20and%20worship%20theme%20professional%20music%20production&width=400&height=300&seq=song-002&orientation=landscape',
    },
  ];

  const privateMinistries = [
    {
      id: 1,
      name: 'ATAN (Adventist Technology Association of Nigeria)',
      focus: 'Technology & Digital Solutions',
      description: 'A tech organization for the church that builds tailored platforms and software linking tech believers across the globe.',
      affiliation: 'Private',
      thumbnail: 'https://readdy.ai/api/search-image?query=modern%20technology%20organization%20with%20diverse%20team%20of%20software%20developers%20and%20engineers%20collaborating%20on%20digital%20platforms%20and%20innovative%20solutions%20in%20a%20bright%20contemporary%20office%20space%20with%20computers%20and%20screens%20showing%20church%20management%20software&width=800&height=600&seq=atan-tech-org&orientation=landscape',
      platforms: ['Website', 'YouTube', 'Mobile App'],
      website: 'https://atan.org',
      links: {
        youtube: 'https://youtube.com/@atan',
        website: 'https://atan.org',
        app: 'https://app.atan.org'
      }
    },
    {
      id: 2,
      name: 'Axios',
      focus: 'Contemporary Worship Music',
      affiliation: 'Adventist-Affiliated',
      description: 'Leading contemporary worship music ministry bringing fresh sounds to Adventist worship across Nigeria',
      platforms: ['Spotify', 'YouTube', 'Apple Music'],
      website: 'https://axios.org',
      thumbnail: 'https://static.readdy.ai/image/b26ccc3ae7fa45b3398224fbc482abd6/5042bf1df586d67fdd53327b39a4165d.jpeg',
    },
    {
      id: 3,
      name: 'ENACTUS Babcock',
      focus: 'Social Entrepreneurship & Community Development',
      affiliation: 'University-Based Ministry',
      description: 'Student-led initiative combining business principles with Christian values to create sustainable community impact',
      platforms: ['Website', 'Instagram', 'LinkedIn'],
      website: 'https://enactus.org',
      thumbnail: 'https://readdy.ai/api/search-image?query=University%20students%20engaged%20in%20community%20service%20project%20combining%20entrepreneurship%20and%20Christian%20mission%20Nigerian%20youth%20helping%20local%20communities%20professional%20documentation%20of%20social%20impact&width=400&height=300&seq=private-003&orientation=landscape',
    },
    {
      id: 4,
      name: 'TMI (Total Member Involvement)',
      focus: 'Evangelism & Mission Training',
      affiliation: 'Adventist-Affiliated',
      description: 'Equipping every church member with tools and training for personal evangelism and community outreach',
      platforms: ['Website', 'Training Portal'],
      website: 'https://tmi.org',
      thumbnail: 'https://readdy.ai/api/search-image?query=church%20members%20training%20session%20for%20evangelism%20with%20materials%20Bibles%20and%20digital%20tools%20community%20outreach%20preparation%20Nigerian%20Adventist%20mission%20training%20program&width=400&height=300&seq=private-004&orientation=landscape',
    },
    {
      id: 5,
      name: 'AV_Radio',
      focus: 'Digital Radio Broadcasting',
      affiliation: 'Adventist-Affiliated',
      description: 'Online radio platform streaming Adventist sermons, music, and programs 24/7 to reach listeners across Nigeria',
      platforms: ['Website', 'Mobile App', 'TuneIn'],
      website: 'https://avradio.org',
      thumbnail: 'https://readdy.ai/api/search-image?query=modern%20radio%20broadcasting%20studio%20with%20microphones%20mixing%20console%20and%20on-air%20sign%20digital%20Christian%20radio%20station%20setup%20professional%20audio%20production%20Nigerian%20context&width=400&height=300&seq=private-005&orientation=landscape',
    },
  ];

  const languages = ['All', 'EN', 'YO', 'HA', 'IG'];

  const filterByLanguage = (items: any[]) => {
    if (selectedLanguage === 'All') return items;
    return items.filter(item => 
      item.language === selectedLanguage || 
      item.translations?.includes(selectedLanguage)
    );
  };

  const handleWatchMovie = (movie: any) => {
    const videoUrl = movie.youtubeId 
      ? `https://www.youtube.com/embed/${movie.youtubeId}?autoplay=1`
      : undefined;
    
    setSelectedVideo({ 
      title: movie.title, 
      videoId: movie.videoId
    });
    setVideoModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 text-white">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              Church Ministries
            </h1>
            <p className="text-xl text-white/95 max-w-3xl mx-auto">
              Explore diverse ministries serving communities across Nigeria
            </p>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveMainTab('church')}
              className={`flex-shrink-0 px-6 py-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeMainTab === 'church'
                  ? 'text-emerald-600 border-emerald-600'
                  : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <i className="ri-community-line mr-2"></i>
              Church Ministries
            </button>
            <button
              onClick={() => setActiveMainTab('sermons')}
              className={`flex-shrink-0 px-6 py-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeMainTab === 'sermons'
                  ? 'text-emerald-600 border-emerald-600'
                  : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <i className="ri-mic-line mr-2"></i>
              Sermons & Messages
            </button>
            <button
              onClick={() => setActiveMainTab('media')}
              className={`flex-shrink-0 px-6 py-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeMainTab === 'media'
                  ? 'text-emerald-600 border-emerald-600'
                  : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <i className="ri-film-line mr-2"></i>
              Media Library
            </button>
            <button
              onClick={() => setActiveMainTab('private')}
              className={`flex-shrink-0 px-6 py-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeMainTab === 'private'
                  ? 'text-emerald-600 border-emerald-600'
                  : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <i className="ri-team-line mr-2"></i>
              Private Ministries & Groups
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Church Ministries Tab */}
        {activeMainTab === 'church' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                Official Church Ministries
              </h2>
              <p className="text-gray-600">
                Standard ministries found in local SDA churches across Nigeria
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {ministries.map(ministry => (
                <MinistryCard key={ministry.id} ministry={ministry} />
              ))}
            </div>
          </div>
        )}

        {/* Sermons & Messages Tab */}
        {activeMainTab === 'sermons' && (
          <div>
            <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                  Sermons & Messages
                </h2>
                <p className="text-gray-600">
                  Inspiring messages from our church leaders
                </p>
              </div>
              <div className="flex gap-2">
                {languages.map(lang => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLanguage(lang)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      selectedLanguage === lang
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filterByLanguage(sermons).map(sermon => (
                <div key={sermon.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden group cursor-pointer">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={sermon.thumbnail}
                      alt={sermon.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-emerald-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                        {sermon.language}
                      </span>
                    </div>
                    {sermon.translations.length > 0 && (
                      <div className="absolute top-3 right-3">
                        <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          +{sermon.translations.length} languages
                        </span>
                      </div>
                    )}
                    <div className="absolute bottom-3 right-3">
                      <span className="px-3 py-1 bg-black/70 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                        {sermon.duration}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                      {sermon.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">{sermon.speaker}</p>
                    <p className="text-xs text-gray-500 mb-4">{sermon.ministry} • {sermon.date}</p>
                    {sermon.translations.length > 0 && (
                      <p className="text-xs text-blue-600 mb-4">
                        Also available in: {sermon.translations.join(', ')}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setSelectedVideo({ 
                          title: sermon.title, 
                          videoId: sermon.videoId 
                        })}
                        className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-play-circle-line mr-2"></i>
                        Play
                      </button>
                      <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer">
                        <i className="ri-download-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Media Library Tab */}
        {activeMainTab === 'media' && (
          <div>
            {/* Media Sub-tabs */}
            <div className="mb-8 flex items-center gap-2 border-b border-gray-200">
              <button
                onClick={() => setActiveMediaSubTab('movies')}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeMediaSubTab === 'movies'
                    ? 'text-emerald-600 border-emerald-600'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
              >
                <i className="ri-movie-line mr-2"></i>
                Movies
              </button>
              <button
                onClick={() => setActiveMediaSubTab('songs')}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeMediaSubTab === 'songs'
                    ? 'text-emerald-600 border-emerald-600'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
              >
                <i className="ri-music-line mr-2"></i>
                Songs / Music
              </button>
            </div>

            {/* Language Filter */}
            <div className="mb-6 flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-700">Filter by language:</span>
              <div className="flex gap-2">
                {languages.map(lang => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLanguage(lang)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      selectedLanguage === lang
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>

            {/* Movies Sub-tab */}
            {activeMediaSubTab === 'movies' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filterByLanguage(movies).map(movie => (
                  <div key={movie.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden group cursor-pointer">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={movie.thumbnail}
                        alt={movie.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className="px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          {movie.language}
                        </span>
                        <span className="px-3 py-1 bg-gray-900 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          {movie.category}
                        </span>
                      </div>
                      {movie.translations.length > 0 && (
                        <div className="absolute top-3 right-3">
                          <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                            +{movie.translations.length} languages
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-3 right-3">
                        <span className="px-3 py-1 bg-black/70 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          {movie.duration}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                        {movie.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        <i className="ri-tv-line mr-1"></i>
                        {movie.platform}
                      </p>
                      {movie.translations.length > 0 && (
                        <p className="text-xs text-blue-600 mb-4">
                          Also available in: {movie.translations.join(', ')}
                        </p>
                      )}
                      <button 
                        onClick={() => handleWatchMovie(movie)}
                        className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-play-circle-line mr-2"></i>
                        Watch Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Songs Sub-tab */}
            {activeMediaSubTab === 'songs' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filterByLanguage(songs).map(song => (
                  <div key={song.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden group cursor-pointer">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={song.thumbnail}
                        alt={song.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 bg-pink-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          {song.language}
                        </span>
                      </div>
                      {song.translations.length > 0 && (
                        <div className="absolute top-3 right-3">
                          <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                            +{song.translations.length} languages
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-3 right-3">
                        <span className="px-3 py-1 bg-black/70 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          {song.duration}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                        {song.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-1">{song.artist}</p>
                      <p className="text-xs text-gray-500 mb-4">
                        <i className="ri-music-2-line mr-1"></i>
                        {song.platform}
                      </p>
                      {song.translations.length > 0 && (
                        <p className="text-xs text-blue-600 mb-4">
                          Also available in: {song.translations.join(', ')}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <button className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-lg text-sm font-semibold hover:bg-pink-700 transition-colors whitespace-nowrap cursor-pointer">
                          <i className="ri-play-circle-line mr-2"></i>
                          Play
                        </button>
                        <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer">
                          <i className="ri-download-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Private Ministries & Groups Tab */}
        {activeMainTab === 'private' && (
          <div>
            {/* Sub-tabs for Private Ministries */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {privateMinistries.map(ministry => (
                  <div key={ministry.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden group">
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={ministry.thumbnail}
                        alt={ministry.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 right-3">
                        <span className="px-3 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          {ministry.affiliation}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                        {ministry.name}
                      </h3>
                      <p className="text-sm text-emerald-600 font-semibold mb-3">
                        <i className="ri-focus-line mr-1"></i>
                        {ministry.focus}
                      </p>
                      <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                        {ministry.description}
                      </p>
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-2">Available on:</p>
                        <div className="flex flex-wrap gap-2">
                          {ministry.platforms.map((platform, index) => (
                            <span 
                              key={index}
                              className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full whitespace-nowrap"
                            >
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                      <a
                        href={ministry.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-external-link-line mr-2"></i>
                        Visit Website
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* Video Modal */}
      {selectedVideo && (
        <VideoModal
          isOpen={videoModalOpen}
          onClose={() => {
            setVideoModalOpen(false);
            setSelectedVideo(null);
          }}
          title={selectedVideo.title}
          videoId={selectedVideo.videoId}
        />
      )}
    </div>
  );
}
